import React, { useRef } from "react"
import moment from "moment"

import { AutoComplete } from "components/AutoComplete"
import { DatePeriodsSelector } from "components/DatePicker"
import { Button } from "components/Button"
import { useProductionScheduleContext } from "./ProductionScheduleContext"
import autoCompleteStyles from "components/AutoComplete/autoCompleteStyles"
import { parseDate } from "utils/dateFormatUtils"
import { useUserRole } from "hooks/useUserRole"

import externalIcon from "../../../assets/images/icons/external-link.svg"
import emptyIcon from "../../../assets/images/schedule/production-warning.svg"
import fullScreenIcon from "../../../assets/images/icons/full-screen.svg"

const EMPTY_TIMELINE_SIZE = 0

const formatProductionLineOptions = data => {
   return (
      data?.map(item => {
         const formatDate = dateString => {
            if (!dateString) return "Ongoing"
            return moment(dateString).format("DD MMM, YY")
         }

         const startDate = formatDate(item.startDate)
         const endDate = formatDate(item.maxTimelineDate || item.endDate)

         return {
            ...item,
            label: `${item.name} - ${startDate} to ${endDate}`,
            value: item.id,
            name: item.name,
         }
      }) || []
   )
}

const ProductionScheduleHeader = ({
   tempRange,
   productionFilter,
   productionListing,
   onDateChange,
   onSetProduction,
   onSearch,
   isDatePickerDisabled,
   isLoading,
   productionDetailsData,
   isExporting = false,
   selectedPeriods = [],
   productionLineId,
   onShowNextInventory = () => {},
   onShowFullScreen = () => {},
   navigateToDetails = () => {},
   onExport = () => {},
}) => {
   const { stageHashMap, timeLineHashMap, loading } =
      useProductionScheduleContext()
   const { isPlantManager } = useUserRole()

   const scrollContainerRef = useRef(null)

   const scrollLeft = () => {
      if (scrollContainerRef.current) {
         scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" })
      }
   }

   const scrollRight = () => {
      if (scrollContainerRef.current) {
         scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" })
      }
   }

   return (
      <>
         <div className="full-screen-header mb-3">
            <div className="admin-inventory-request-form-fields">
               <div className="w-100 production-line-header">
                  <div className="d-flex justify-content-start align-items-center flex-wrap">
                     <div className="min-200-w margin-right-handle production-line-param">
                        <AutoComplete
                           placeholder="Select Production Line"
                           value={productionFilter}
                           customStyles={autoCompleteStyles.STYLE_SIX}
                           onInputChange={val => onSearch(val)}
                           options={formatProductionLineOptions(
                              productionListing.data
                           )}
                           onChange={val => onSetProduction(val)}
                           closeMenuOnSelect={true}
                           isLoading={productionListing?.fetching}
                           classNamePrefix="status-header-search-admin"
                           menuPortalTarget={document.body}
                           formatOptionLabel={(option, { context }) =>
                              context === "value" ? option.name : option.label
                           }
                        />
                     </div>
                     <div className="mx-2 d-flex production-line-param">
                        <DatePeriodsSelector
                           key={productionFilter?.id || "no-production-line"}
                           onChange={onDateChange}
                           disabled={
                              isDatePickerDisabled ||
                              isLoading ||
                              loading ||
                              !!Object.keys(stageHashMap).length
                           }
                           isLoading={isLoading || loading}
                           minDate={parseDate(productionDetailsData?.startDate)}
                           maxDate={
                              productionDetailsData?.maxTimelineDate
                                 ? parseDate(
                                      productionDetailsData?.maxTimelineDate
                                   )
                                 : parseDate(productionDetailsData?.endDate)
                           }
                           productionLineId={productionLineId}
                        />
                     </div>
                  </div>

                  <div className="d-flex flex-wrap justify-content-end align-items-center gap-1 production-line-header-buttons ">
                     {timeLineHashMap?.size > EMPTY_TIMELINE_SIZE && (
                        <Button
                           className="rounded-full border-1 border-black d-flex align-items-center justify-content-center flex-shrink-0 tooltip-container"
                           styles={{ width: "32px", minWidth: "32px" }}
                           title={
                              <>
                                 <img
                                    alt="slot-card-icon "
                                    src={fullScreenIcon}
                                    width={15}
                                    height={15}
                                 />
                                 <span className="custom-tooltip custom-tooltip-bottom">
                                    View Full Screen
                                 </span>
                              </>
                           }
                           onClick={onShowFullScreen}
                        />
                     )}
                     {productionFilter?.id &&
                        !isPlantManager &&
                        timeLineHashMap?.size > EMPTY_TIMELINE_SIZE && (
                           <div className="production-line-header-buttons-configurations flex-shrink-0 tooltip-container">
                              {" "}
                              <span
                                 className="pointer  d-flex align-items-center justify-content-center gap-2 tooltip-target "
                                 onClick={navigateToDetails}
                              >
                                 <img
                                    src={externalIcon}
                                    width={25}
                                    height={25}
                                    alt="view-icon"
                                 />
                                 <span className="custom-tooltip custom-tooltip-bottom">
                                    View Configurations
                                 </span>
                              </span>
                           </div>
                        )}
                     {timeLineHashMap?.size > EMPTY_TIMELINE_SIZE && (
                        <Button
                           className={"export-button"}
                           onClick={onExport}
                           title={<p>Export</p>}
                           isLoading={isExporting}
                        />
                     )}
                     {timeLineHashMap?.size > EMPTY_TIMELINE_SIZE && (
                        <Button
                           title={
                              <div className="d-flex gap-2 align-items-center justify-content-center">
                                 <img
                                    alt="slot-card-icon"
                                    src={emptyIcon}
                                    width={15}
                                    height={15}
                                 />
                                 <p>Show Inventory Warning</p>
                              </div>
                           }
                           className={"production-button flex-shrink-0 "}
                           onClick={onShowNextInventory}
                        />
                     )}
                  </div>
               </div>
            </div>
         </div>

         <div className="selected-periods-chips-container">
            {selectedPeriods.length > 0 && (
               <div className="periods-scroll-wrapper">
                  <button
                     className="scroll-arrow scroll-arrow-left"
                     onClick={scrollLeft}
                     aria-label="Scroll left"
                  >
                     ‹
                  </button>

                  <div
                     className="periods-inner-container"
                     ref={scrollContainerRef}
                  >
                     <div className="combined-range-badge">
                        <span className="combined-range-text">
                           {(() => {
                              const startDate = moment(
                                 productionDetailsData?.startDate
                              )
                              const endDate = moment(
                                 productionDetailsData?.maxTimelineDate ||
                                    productionDetailsData?.endDate
                              )

                              return `${startDate.format(
                                 "MMM DD, YYYY"
                              )} - ${endDate.format("MMM DD, YYYY")}`
                           })()}
                        </span>
                     </div>

                     {selectedPeriods
                        .sort((a, b) =>
                           moment(a.startDate).diff(moment(b.startDate))
                        )
                        .map((period, index) => (
                           <div key={index} className="selected-period-chip">
                              <span className="period-chip-text">
                                 {`${moment(period.startDate).format(
                                    "MMM DD"
                                 )} - ${moment(period.endDate).format(
                                    "MMM DD"
                                 )}`}
                              </span>
                           </div>
                        ))}
                  </div>

                  <button
                     className="scroll-arrow scroll-arrow-right"
                     onClick={scrollRight}
                     aria-label="Scroll right"
                  >
                     ›
                  </button>
               </div>
            )}
         </div>
      </>
   )
}

export default ProductionScheduleHeader
